import { createAction, props } from '@ngrx/store';
import { Timestamp } from '@angular/fire/firestore';
import { IGlobalError } from '../../../shared/shared-components/components/global-error-display/models/global-error';
import { StockItem } from '../../../shared-utilities/models-old/datastructures';
import { ILastStockTakeSyncStatus } from '../models/stock-take-date-sync.interface';
import { IStockTakeDashboardData } from '../models/stock-take-dashboard.interface';
import {
  IStockItemApiData,
  IStockTake,
  IStockTakeCreate,
  IStockTakeFinalFigure,
  IStockTakeItem,
  IStockTakeScanSession,
  IStockTakeScheduleEntry,
  StockTakeStatus,
} from '../models';
import { StockTakeReportType } from '../services';
import { ISharedLoadingProgressPresentation } from '../../../shared/shared-models/loading/shared-loading-progress.interface';

//#region Load Stock Takes

export const loadStockTakes = createAction(
  '[Stock Takes] Load Stock Takes',
  props<{ storeId: string; status?: StockTakeStatus | StockTakeStatus[] }>(),
);

export const loadStockTakesSuccess = createAction(
  '[Stock Takes] Load Stock Takes Success',
  props<{ storeId: string; adjustments: IStockTake[] }>(),
);

export const loadStockTakesFailure = createAction(
  '[Stock Takes] Load Stock Takes Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Stock Take Status Counts

export const loadStockTakeStatusCounts = createAction(
  '[Stock Takes] Load Status Counts',
  props<{ storeId: string }>(),
);

export const loadStockTakeStatusCountsSuccess = createAction(
  '[Stock Takes] Load Status Counts Success',
  props<{ storeId: string; counts: Record<string, number> }>(),
);

export const loadStockTakeStatusCountsFailure = createAction(
  '[Stock Takes] Load Status Counts Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Paginated Completed Stock Takes

/**
 * `direction = 'first'` resets the cursor and loads the first page.
 * `direction = 'next'` appends the next page using the existing cursor.
 */
export const loadCompletedStockTakesPage = createAction(
  '[Stock Takes] Load Completed Page',
  props<{ storeId: string; direction: 'first' | 'next' }>(),
);

export const loadCompletedStockTakesPageSuccess = createAction(
  '[Stock Takes] Load Completed Page Success',
  props<{
    append: boolean;
    hasMore: boolean;
    items: IStockTake[];
    storeId: string;
  }>(),
);

export const loadCompletedStockTakesPageFailure = createAction(
  '[Stock Takes] Load Completed Page Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Load Stock Adjustment Details

export const loadStockTakeDetails = createAction(
  '[Stock Takes] Load Stock Adjustment Details',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const loadStockTakeDetailsSuccess = createAction(
  '[Stock Takes] Load Stock Adjustment Details Success',
  props<{ adjustment: IStockTake }>(),
);

export const loadStockTakeDetailsFailure = createAction(
  '[Stock Takes] Load Stock Adjustment Details Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Load Stock Adjustment Items

export const loadStockTakeItems = createAction(
  '[Stock Takes] Load Stock Adjustment Items',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const loadStockTakeItemsSuccess = createAction(
  '[Stock Takes] Load Stock Adjustment Items Success',
  props<{ adjustmentId: string; items: IStockTakeItem[] }>(),
);

export const loadStockTakeItemsFailure = createAction(
  '[Stock Takes] Load Stock Adjustment Items Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Create Stock Adjustment

export const createStockTake = createAction(
  '[Stock Takes] Create Stock Adjustment',
  props<{
    storeId: string;
    data: IStockTakeCreate;
  }>(),
);

export const createStockTakeSuccess = createAction(
  '[Stock Takes] Create Stock Adjustment Success',
  props<{
    adjustmentId: string;
    storeId: string;
    countUncountedDays?: number;
    uncountedItemLimit?: number;
    uncountedSortOrder?: IStockTakeCreate['uncountedSortOrder'];
    filterGroup?: IStockTakeCreate['filterGroup'];
    filterGroups?: IStockTakeCreate['filterGroups'];
    filterSortOrder?: IStockTakeCreate['filterSortOrder'];
    itemLimit?: number;
  }>(),
);

export const createStockTakeFailure = createAction(
  '[Stock Takes] Create Stock Adjustment Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Update Stock Adjustment

export const updateStockTake = createAction(
  '[Stock Takes] Update Stock Adjustment',
  props<{
    storeId: string;
    adjustmentId: string;
    updates: Partial<IStockTake>;
  }>(),
);

export const updateStockTakeSuccess = createAction(
  '[Stock Takes] Update Stock Adjustment Success',
  props<{
    storeId: string;
    adjustmentId: string;
    updates: Partial<IStockTake>;
  }>(),
);

export const updateStockTakeFailure = createAction(
  '[Stock Takes] Update Stock Adjustment Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Delete Stock Adjustment

export const deleteStockTake = createAction(
  '[Stock Takes] Delete Stock Adjustment',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const deleteStockTakeSuccess = createAction(
  '[Stock Takes] Delete Stock Adjustment Success',
  props<{ adjustmentId: string; storeId: string }>(),
);

export const deleteStockTakeFailure = createAction(
  '[Stock Takes] Delete Stock Adjustment Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Mobile Scan Session

export const startScanSession = createAction(
  '[Stock Takes] Start Scan Session',
  props<{ storeId: string; adjustmentId: string; userId: string }>(),
);

export const startScanSessionSuccess = createAction(
  '[Stock Takes] Start Scan Session Success',
  props<{ session: IStockTakeScanSession }>(),
);

export const startScanSessionFailure = createAction(
  '[Stock Takes] Start Scan Session Failure',
  props<{ error: IGlobalError }>(),
);

export const scanBarcodeSuccess = createAction(
  '[Stock Takes] Scan Barcode Success',
  props<{ stockItem: StockItem; barcode: string; timestamp: Timestamp }>(),
);

export const updateScannedItemQuantity = createAction(
  '[Stock Takes] Update Scanned Item Quantity',
  props<{ stockItemId: string; quantity: number }>(),
);

export const removeScannedItem = createAction(
  '[Stock Takes] Remove Scanned Item',
  props<{ stockItemId: string }>(),
);

export const completeScanSession = createAction(
  '[Stock Takes] Complete Scan Session',
  props<{ storeId: string; adjustmentId: string; userId: string }>(),
);

export const completeScanSessionSuccess = createAction(
  '[Stock Takes] Complete Scan Session Success',
  props<{ adjustmentId: string }>(),
);

export const completeScanSessionFailure = createAction(
  '[Stock Takes] Complete Scan Session Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Process Stock Adjustment

export const processStockTake = createAction(
  '[Stock Takes] Process Stock Adjustment',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const processStockTakeSuccess = createAction(
  '[Stock Takes] Process Stock Adjustment Success',
  props<{ adjustmentId: string }>(),
);

export const processStockTakeFailure = createAction(
  '[Stock Takes] Process Stock Adjustment Failure',
  props<{ error: IGlobalError }>(),
);

export const retryFailedStockTakeProcessing = createAction(
  '[Stock Takes] Retry Failed Processing Message',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const retryFailedStockTakeProcessingSuccess = createAction(
  '[Stock Takes] Retry Failed Processing Success',
);

export const retryFailedStockTakeProcessingFailure = createAction(
  '[Stock Takes] Retry Failed Processing Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Complete Stock Adjustment

export const completeStockTake = createAction(
  '[Stock Takes] Complete Stock Adjustment',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const completeStockTakeSuccess = createAction(
  '[Stock Takes] Complete Stock Adjustment Success',
  props<{ adjustmentId: string }>(),
);

export const completeStockTakeFailure = createAction(
  '[Stock Takes] Complete Stock Adjustment Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Generate Reports

export const generateStockTakeReport = createAction(
  '[Stock Takes] Generate Stock Adjustment Report',
  props<{
    storeId: string;
    adjustmentId: string;
    reportType: StockTakeReportType;
  }>(),
);

export const generateStockTakeReportSuccess = createAction(
  '[Stock Takes] Generate Stock Adjustment Report Success',
);

export const generateStockTakeReportFailure = createAction(
  '[Stock Takes] Generate Stock Adjustment Report Failure',
  props<{ error: IGlobalError }>(),
);

export const exportStockTakeCsv = createAction(
  '[Stock Takes] Export Stock Adjustment CSV',
  props<{ storeId: string; adjustmentId: string }>(),
);

//#endregion

//#region Load Stock Take Item Details

export const loadStockTakeItemDetails = createAction(
  '[Stock Takes] Load Stock Take Item Details',
  props<{ adjustmentId: string; storeId: string; codes: string[] }>(),
);

export const loadStockTakeItemDetailsSuccess = createAction(
  '[Stock Takes] Load Stock Take Item Details Success',
  props<{ adjustmentId: string; apiData: Record<string, IStockItemApiData>; missingCodes: string[] }>(),
);

export const loadStockTakeItemDetailsFailure = createAction(
  '[Stock Takes] Load Stock Take Item Details Failure',
  props<{ adjustmentId: string; error: IGlobalError }>(),
);

export const refreshStockTakeItemDetails = createAction(
  '[Stock Takes] Refresh Stock Take Item Details',
  props<{ adjustmentId: string; storeId: string }>(),
);

export const removeItemsFromStockTake = createAction(
  '[Stock Takes] Remove Items From Stock Take',
  props<{ storeId: string; adjustmentId: string; codes: string[] }>(),
);

export const removeItemsFromStockTakeSuccess = createAction(
  '[Stock Takes] Remove Items From Stock Take Success',
  props<{ adjustmentId: string; codes?: string[] }>(),
);

export const removeItemsFromStockTakeFailure = createAction(
  '[Stock Takes] Remove Items From Stock Take Failure',
  props<{ error: IGlobalError }>(),
);

export const resetStockTakeItemToPending = createAction(
  '[Stock Takes] Reset Item To Pending',
  props<{ storeId: string; adjustmentId: string; itemId: string }>(),
);

export const resetStockTakeItemToPendingSuccess = createAction(
  '[Stock Takes] Reset Item To Pending Success',
  props<{ adjustmentId: string; itemId: string }>(),
);

export const resetStockTakeItemToPendingFailure = createAction(
  '[Stock Takes] Reset Item To Pending Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Stock Take Item Mutations (grid loading)

/**
 * Decrements the in-flight mutation counter after Firestore item snapshots settle.
 * Dispatched from an effect — not from components.
 */
export const acknowledgeStockTakeItemMutationSync = createAction(
  '[Stock Takes] Acknowledge Item Mutation Firestore Sync',
);

/**
 * Firestore item listener has been quiet long enough to refresh the grid.
 */
export const stockTakeItemsListenerSettled = createAction(
  '[Stock Takes] Stock Take Items Listener Settled',
);

//#endregion

//#region Update Single Item Count

export const updateStockTakeItemCount = createAction(
  '[Stock Takes] Update Item Count',
  props<{
    storeId: string;
    adjustmentId: string;
    itemId: string;
    qtyCounted: number;
    previousQtyCounted: number;
    isFirstCount: boolean;
    overrideCountHistory?: boolean;
    binLocation?: string;
  }>(),
);

export const updateStockTakeItemCountSuccess = createAction(
  '[Stock Takes] Update Item Count Success',
  props<{
    adjustmentId: string;
    itemId: string;
    qtyCounted: number;
    previousQtyCounted: number;
    isFirstCount: boolean;
    overrideCountHistory?: boolean;
  }>(),
);

export const updateStockTakeItemCountFailure = createAction(
  '[Stock Takes] Update Item Count Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Mark every PENDING (not recount) line on an open stock take as counted with quantity zero.
 *
 * Dispatched from the pending items modal after the user confirms the bulk action.
 */
export const countAllPendingItemsAsZero = createAction(
  '[Stock Takes] Count All Pending Items As Zero',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const countAllPendingItemsAsZeroSuccess = createAction(
  '[Stock Takes] Count All Pending Items As Zero Success',
  props<{ adjustmentId: string; updatedCount: number }>(),
);

export const countAllPendingItemsAsZeroFailure = createAction(
  '[Stock Takes] Count All Pending Items As Zero Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Add Unique Item (scanned but not in list)

export const addUniqueItemToStockTake = createAction(
  '[Stock Takes] Add Unique Item',
  props<{ storeId: string; adjustmentId: string; code: string; qtyCounted: number; binLocation?: string }>(),
);

export const addUniqueItemToStockTakeSuccess = createAction(
  '[Stock Takes] Add Unique Item Success',
  props<{ adjustmentId: string }>(),
);

export const addUniqueItemToStockTakeFailure = createAction(
  '[Stock Takes] Add Unique Item Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Barcode Scan (mobile)

export const stockTakeBarcodeDetected = createAction(
  '[Stock Takes] Barcode Detected',
  props<{ code: string }>(),
);

export const clearPendingScannedCode = createAction(
  '[Stock Takes] Clear Pending Scanned Code',
);

export const stockTakeBarcodeNotFound = createAction(
  '[Stock Takes] Barcode Not Found In Stock',
  props<{ barcode: string }>(),
);

export const clearBarcodeNotFound = createAction(
  '[Stock Takes] Clear Barcode Not Found',
);

export const assignUnknownStockTakeBarcode = createAction(
  '[Stock Takes] Assign Unknown Barcode To Stock Item',
  props<{
    storeId: string;
    adjustmentId: string;
    stockItem: StockItem;
    scannedBarcode: string;
    mode: 'primary' | 'multiple';
  }>(),
);

export const assignUnknownStockTakeBarcodeFailure = createAction(
  '[Stock Takes] Assign Unknown Barcode Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Add Items To Stock Take

export const addItemsToStockTake = createAction(
  '[Stock Takes] Add Items To Stock Take',
  props<{ storeId: string; adjustmentId: string; items: { code: string; category?: string | number }[] }>(),
);

export const addItemsToStockTakeSuccess = createAction(
  '[Stock Takes] Add Items To Stock Take Success',
  props<{ adjustmentId: string }>(),
);

export const addItemsToStockTakeFailure = createAction(
  '[Stock Takes] Add Items To Stock Take Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Filters

export const setStatusFilter = createAction(
  '[Stock Takes] Set Status Filter',
  props<{ status: string | null }>(),
);

//#endregion

//#region Transactions Timestamp

export const loadTransactionsTimestamp = createAction(
  '[Stock Takes] Load Transactions Timestamp',
  props<{ storeId: string }>(),
);

export const loadTransactionsTimestampSuccess = createAction(
  '[Stock Takes] Load Transactions Timestamp Success',
  props<{ transactions: Date | null; delColIssuesTran: Date | null; stockTimestamp: Date | null }>(),
);

export const loadTransactionsTimestampFailure = createAction(
  '[Stock Takes] Load Transactions Timestamp Failure',
);

//#endregion

//#region Mark Stock Take As Prepared

export const markStockTakeAsPrepared = createAction(
  '[Stock Takes] Mark Stock Take As Prepared',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const markStockTakeAsPreparedSuccess = createAction(
  '[Stock Takes] Mark Stock Take As Prepared Success',
  props<{ adjustmentId: string }>(),
);

export const markStockTakeAsPreparedFailure = createAction(
  '[Stock Takes] Mark Stock Take As Prepared Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Move a prepared stock take back to in progress.
 */
export const markStockTakeAsInProgress = createAction(
  '[Stock Takes] Mark Stock Take As In Progress',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const markStockTakeAsInProgressSuccess = createAction(
  '[Stock Takes] Mark Stock Take As In Progress Success',
  props<{ adjustmentId: string }>(),
);

export const markStockTakeAsInProgressFailure = createAction(
  '[Stock Takes] Mark Stock Take As In Progress Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * A prepared stock take was moved back to in progress because a pending line was counted or removed.
 */
export const stockTakeRevertedToInProgress = createAction(
  '[Stock Takes] Stock Take Reverted To In Progress',
  props<{ adjustmentId: string }>(),
);

//#endregion

//#region Mark Item For Recount

export const markItemForRecount = createAction(
  '[Stock Takes] Mark Item For Recount',
  props<{ storeId: string; adjustmentId: string; itemId: string }>(),
);

export const markItemForRecountSuccess = createAction(
  '[Stock Takes] Mark Item For Recount Success',
  props<{ adjustmentId: string; itemId: string; resetParentToInProgress: boolean }>(),
);

export const markItemForRecountFailure = createAction(
  '[Stock Takes] Mark Item For Recount Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Load Stock Movement

export const loadStockMovementSuccess = createAction(
  '[Stock Takes] Load Stock Movement Success',
  props<{ adjustmentId: string; results: { code: string; stockMovement: number; delColMovement: number }[] }>(),
);

export const loadStockMovementFailure = createAction(
  '[Stock Takes] Load Stock Movement Failure',
  props<{ error: IGlobalError }>(),
);

export const clearMovementOutOfSync = createAction(
  '[Stock Takes] Clear Movement Out Of Sync',
);

//#endregion

//#region Recount From Report View

/**
 * Add a single item (by code) as PENDING to a different (target) stock take.
 * Used when recounting from a finalised/report-mode stock take.
 */
export const addRecountItemToStockTake = createAction(
  '[Stock Takes] Add Recount Item To Stock Take',
  props<{ storeId: string; targetAdjustmentId: string; itemCode: string }>(),
);

export const addRecountItemToStockTakeSuccess = createAction(
  '[Stock Takes] Add Recount Item To Stock Take Success',
  props<{ targetAdjustmentId: string }>(),
);

export const addRecountItemToStockTakeFailure = createAction(
  '[Stock Takes] Add Recount Item To Stock Take Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Create a brand-new stock take and immediately add a single item as PENDING to it.
 * Used when "Create New Stock Take" is selected from the recount target modal.
 */
export const createStockTakeForRecount = createAction(
  '[Stock Takes] Create Stock Take For Recount',
  props<{ storeId: string; data: IStockTakeCreate; itemCode: string }>(),
);

export const createStockTakeForRecountSuccess = createAction(
  '[Stock Takes] Create Stock Take For Recount Success',
  props<{ storeId: string }>(),
);

export const createStockTakeForRecountFailure = createAction(
  '[Stock Takes] Create Stock Take For Recount Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Create a new stock take from deferred items on an in-progress stock take being processed.
 * Copies metadata from the source stock take and moves pending/recount lines to the new stock take.
 */
export const createStockTakeFromProcess = createAction(
  '[Stock Takes] Create Stock Take From Process',
  props<{
    storeId: string;
    sourceAdjustmentId: string;
    itemCodes: string[];
    data: IStockTakeCreate;
  }>(),
);

export const createStockTakeFromProcessSuccess = createAction(
  '[Stock Takes] Create Stock Take From Process Success',
  props<{
    storeId: string;
    sourceAdjustmentId: string;
    newAdjustmentId: string;
    itemCodes: string[];
  }>(),
);

export const createStockTakeFromProcessFailure = createAction(
  '[Stock Takes] Create Stock Take From Process Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Load Finalised Items (report view for submitted stock takes)

export const loadFinalisedItems = createAction(
  '[Stock Takes] Load Finalised Items',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const loadFinalisedItemsSuccess = createAction(
  '[Stock Takes] Load Finalised Items Success',
  props<{ adjustmentId: string; items: IStockTakeFinalFigure[] }>(),
);

export const loadFinalisedItemsFailure = createAction(
  '[Stock Takes] Load Finalised Items Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Run Stock Takes From Schedule

export const runStockTakesFromSchedule = createAction(
  '[Stock Takes] Run Stock Takes From Schedule',
  props<{ storeId: string; schedules: IStockTakeScheduleEntry[]; includeDelCol?: boolean }>(),
);

export const runStockTakesFromScheduleSuccess = createAction(
  '[Stock Takes] Run Stock Takes From Schedule Success',
  props<{ storeId: string }>(),
);

export const runStockTakesFromScheduleFailure = createAction(
  '[Stock Takes] Run Stock Takes From Schedule Failure',
  props<{ error: IGlobalError }>(),
);

export const setStockTakePopulationProgress = createAction(
  '[Stock Takes] Set Population Progress',
  props<{ progress: ISharedLoadingProgressPresentation | null }>(),
);

//#endregion

//#region Dashboard quick actions

export const createNeverCountedStockTake = createAction(
  '[Stock Takes] Create Never Counted Stock Take',
  props<{ storeId: string }>(),
);

export const createNeverCountedStockTakeSuccess = createAction(
  '[Stock Takes] Create Never Counted Stock Take Success',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const createNeverCountedStockTakeFailure = createAction(
  '[Stock Takes] Create Never Counted Stock Take Failure',
  props<{ error: IGlobalError }>(),
);

export const createHighRiskStockTake = createAction(
  '[Stock Takes] Create High Risk Stock Take',
  props<{ storeId: string }>(),
);

export const createHighRiskStockTakeSuccess = createAction(
  '[Stock Takes] Create High Risk Stock Take Success',
  props<{ storeId: string; adjustmentId: string }>(),
);

export const createHighRiskStockTakeFailure = createAction(
  '[Stock Takes] Create High Risk Stock Take Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Last stock take sync

export const loadLastStockTakeSyncStatus = createAction(
  '[Stock Takes] Load Last Stock Take Sync Status',
  props<{ storeId: string; force?: boolean }>(),
);

export const setLastStockTakeSyncStatus = createAction(
  '[Stock Takes] Set Last Stock Take Sync Status',
  props<{
    status: ILastStockTakeSyncStatus;
    itemsSynced?: number;
    totalItems?: number;
    /** SUCCESS only — observation `completedAt` (stable after sync finishes). */
    syncCompletedAt?: Timestamp;
    /** PENDING only — observation `executedAt` (updates during progress). */
    processingStartedAt?: Timestamp;
    errorMessage?: string | null;
  }>(),
);

export const requestLastStockTakeSync = createAction(
  '[Stock Takes] Request Last Stock Take Sync',
  props<{ storeId: string; force?: boolean }>(),
);

export const clearStockInitObservationPath = createAction(
  '[Stock Takes] Clear Stock Init Observation Path',
);

export const requestLastStockTakeSyncSuccess = createAction(
  '[Stock Takes] Request Last Stock Take Sync Success',
  props<{
    message: string;
    observationPath: string;
    status: 'PROCESSING' | 'SUCCESS';
  }>(),
);

export const requestLastStockTakeSyncFailure = createAction(
  '[Stock Takes] Request Last Stock Take Sync Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Dashboard

export const loadStockTakeDashboard = createAction(
  '[Stock Takes] Load Dashboard',
  props<{ storeId: string }>(),
);

export const loadStockTakeDashboardSuccess = createAction(
  '[Stock Takes] Load Dashboard Success',
  props<{
    data: IStockTakeDashboardData;
    typesenseMissingStockTakeDateCount: number;
    typesenseStockTakeDateSchemaReady: boolean;
  }>(),
);

export const loadStockTakeDashboardFailure = createAction(
  '[Stock Takes] Load Dashboard Failure',
  props<{ error: IGlobalError }>(),
);

export const checkTypesenseStockTakeDateGate = createAction(
  '[Stock Takes] Check Typesense Stock Take Date Gate',
  props<{ storeId: string }>(),
);

export const checkTypesenseStockTakeDateGateSuccess = createAction(
  '[Stock Takes] Check Typesense Stock Take Date Gate Success',
  props<{
    typesenseMissingStockTakeDateCount: number;
    typesenseStockTakeDateSchemaReady: boolean;
  }>(),
);

export const checkTypesenseStockTakeDateGateFailure = createAction(
  '[Stock Takes] Check Typesense Stock Take Date Gate Failure',
  props<{ error: IGlobalError }>(),
);

//#endregion

//#region Clear State

export const clearCurrentAdjustment = createAction(
  '[Stock Takes] Clear Current Adjustment',
);

export const clearScanSession = createAction(
  '[Stock Takes] Clear Scan Session',
);

//#endregion
